import { useQuery } from '@apollo/client';
import {
  BuildingOffice2Icon,
  GlobeAltIcon,
  MapPinIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { LoadingOrError, mapHref } from '@retainerclub/common';
import { GET_PARTNER_LOC } from '@retainerclub/customer-api';
import { useAccount } from '@retainerclub/hooks';
import { useState } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';

function FooterPartnerInfo() {
  const [location, setLocation] = useState(null);
  const { partnerLocationId } = useAccount();

  const { error, loading } = useQuery(GET_PARTNER_LOC, {
    variables: {
      id: partnerLocationId,
    },
    skip: partnerLocationId === null || partnerLocationId === undefined,
    onCompleted: (data) => {
      setLocation(data.partnerLocation);
    },
  });

  if (!partnerLocationId) {
    return null;
  }

  const shouldRender = LoadingOrError({
    loading,
    error,
    data: { partnerLocation: location },
    dataKey: 'partnerLocation',
    title: 'Partner Location',
  });

  if (shouldRender === true) {
    return (
      <div className="mb-4 w-full">
        <div className="mb-4 border-white/10 border-b pb-2">
          <h2 className="text-center font-bold text-lg text-rcgray-600 tracking-tight sm:text-2xl">
            Your Provider
          </h2>
        </div>
        <div className="mt-4 flex flex-col items-center gap-4 md:flex-row md:items-start md:justify-center">
          <dl className="flex flex-row flex-wrap items-start justify-center text-gray-400 text-sm leading-5">
            <div className="flex basis-1/2 gap-x-4 p-4 md:basis-auto">
              <dt className="flex-none">
                <BuildingOffice2Icon
                  className="size-6 text-rcprimary-700"
                  aria-hidden="true"
                />
              </dt>
              <dd className="font-medium text-base text-rcgray-500">
                {location?.name}
              </dd>
            </div>
            <div className="flex basis-1/2 gap-x-4 p-4 md:basis-auto">
              <dt className="flex-none">
                <MapPinIcon
                  className="size-6 text-rcprimary-700"
                  aria-hidden="true"
                />
              </dt>
              <dd>
                <a
                  className="text-rcgray-500 hover:text-rcgray-300"
                  href={mapHref(null, null, location?.address)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {location?.address?.address}
                  <br />
                  {location?.address?.address2 && (
                    <>
                      {location?.address?.address2}
                      <br />
                    </>
                  )}
                  {location?.address?.city}, {location?.address?.region}&nbsp;
                  {location?.address?.postalCode}
                </a>
              </dd>
            </div>
            {location?.phone && (
              <div className="flex basis-1/2 items-center gap-x-4 p-4 md:basis-auto">
                <dt className="flex-none">
                  <PhoneIcon
                    className="size-6 text-rcprimary-700"
                    aria-hidden="true"
                  />
                </dt>
                <dd>
                  <a
                    className="text-rcgray-500 hover:text-rcgray-300"
                    href={`tel:${location?.phone}`}
                  >
                    {formatPhoneNumber(location?.phone)}
                  </a>
                </dd>
              </div>
            )}
            {location?.website && (
              <div className="flex basis-1/2 gap-x-4 p-4 md:basis-auto">
                <dt className="flex-none">
                  <GlobeAltIcon
                    className="size-6 text-rcprimary-700"
                    aria-hidden="true"
                  />
                </dt>
                <dd>
                  <a
                    className="text-rcgray-500 hover:text-rcgray-300"
                    href={`//${location?.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Provider Website
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    );
  }

  return (loading || error) && location === null ? shouldRender : null;
}

export default FooterPartnerInfo;
